import { capitalCase } from 'change-case';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Card, Stack, Link, Tooltip, Container, Typography } from '@mui/material';
// routes
import { PATH_AUTH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import useLocales from '../../hooks/useLocales';
// components
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import Image from '../../components/Image';
import SvgIconStyle from '../../components/SvgIconStyle';
import LanguagePopover from '../../components/LanguagePopover';
// sections
import { LoginForm } from '../../sections/auth/login';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: theme.spacing(2, 0, 2, 2),
  padding: theme.spacing(2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function Login() {
  const { translate } = useLocales();
  const { method } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');

  return (
    <Page title={translate('login.page_title')}>
      <RootStyle>
        <HeaderStyle>
          <Logo />
          {smUp && (
            <>
              <Typography variant="body2" sx={{ mt: { md: -2 } }}>
                {`${translate('login.dont_have_account')} `}
                <Link variant="subtitle2" component={RouterLink} to={PATH_AUTH.register}>
                  {translate('login.get_started')}
                </Link>
              </Typography>
            </>
          )}
        </HeaderStyle>

        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              {translate('login.hi_welcome_back')}
            </Typography>
            <Image alt="login" src="/icons/draw_login.svg" />
          </SectionStyle>
        )}

        <Container maxWidth="sm">
          <ContentStyle>
            <Stack direction="row" alignItems="center" sx={{ mb: 5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" gutterBottom>
                  {translate('login.sign_into_app')}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>{translate('login.enter_detail')}</Typography>
              </Box>

              <Tooltip title={capitalCase(method)} placement="right">
                <>
                  <SvgIconStyle src="/icons/checklist.svg" sx={{ width: 32, height: 32, color: 'primary.main' }} />
                </>
              </Tooltip>
            </Stack>

            <LoginForm />

            {!smUp && (
              <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                {`${translate('login.dont_have_account')} `}
                <Link variant="subtitle2" component={RouterLink} to={PATH_AUTH.register}>
                  {translate('login.get_started')}
                </Link>
              </Typography>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>

      <Box sx={{ position: 'fixed', bottom: 30, right: 30 }}>
        <LanguagePopover />
      </Box>
    </Page>
  );
}
